import React, { useEffect, useRef } from "react";
import lottie from "lottie-web";
import styles from "./HalloweenDecor.module.css";
import { SPIDER_PATH } from "./SeasonalLottie";

// SCRUM-922: spider on its silk thread (hanging-spider.lottie, 240 frames).
// The clip drops the spider down (0-150), lets it dangle, then climbs back up
// (150-240, ending where it started). Instead of freezing it mid-swing, each
// cycle it rests at frame 150 - hanging low and straight - while the whole
// thread sways gently (CSS), for a random number of whole sway cycles, then
// climbs and drops again. Speed and rest length differ per spider, so two
// spiders never move in step.
const REST_FRAME = 150; // hanging low, thread vertical (swing angle 0)
const END_FRAME = 240;
const SWAY_MS = 2400; // one full sway cycle - must match .spiderSway in the CSS

const randInt = (min, max) => min + Math.floor(Math.random() * (max - min + 1));

const HangingSpider = ({
  className,
  speed = 1,
  restCycles = [1, 3], // random rest = this many sway cycles
  startDelayMs = 0,
}) => {
  const swayRef = useRef(null);

  useEffect(() => {
    const container = swayRef.current;
    if (!container) return undefined;

    const reduceMotion =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const anim = lottie.loadAnimation({
      container,
      renderer: "svg",
      loop: false,
      autoplay: false,
      path: SPIDER_PATH,
      rendererSettings: { preserveAspectRatio: "xMidYMin meet" },
    });
    anim.setSpeed(speed);

    let timer = null;
    let phase = "down";

    const playDown = () => {
      phase = "down";
      anim.playSegments([0, REST_FRAME], true);
    };

    const rest = () => {
      phase = "rest";
      anim.goToAndStop(REST_FRAME, true);
      container.classList.add(styles.spiderSway);
      const cycles = randInt(restCycles[0], restCycles[1]);
      timer = setTimeout(() => {
        container.classList.remove(styles.spiderSway);
        phase = "up";
        anim.playSegments([REST_FRAME, END_FRAME], true);
      }, cycles * SWAY_MS);
    };

    const onComplete = () => {
      if (phase === "down") rest();
      else if (phase === "up") playDown();
    };

    const onReady = () => {
      if (reduceMotion) {
        anim.goToAndStop(REST_FRAME, true); // still, hanging straight
        return;
      }
      timer = setTimeout(playDown, startDelayMs);
    };
    // Purely decorative: if the asset fails to load, render nothing.
    const onFail = () => anim.destroy();

    anim.addEventListener("DOMLoaded", onReady);
    anim.addEventListener("complete", onComplete);
    anim.addEventListener("data_failed", onFail);

    return () => {
      clearTimeout(timer);
      anim.removeEventListener("DOMLoaded", onReady);
      anim.removeEventListener("complete", onComplete);
      anim.removeEventListener("data_failed", onFail);
      anim.destroy();
    };
  }, [speed, restCycles, startDelayMs]);

  // Outer box is positioned (and centred with a transform) by the caller; the
  // inner box is free to sway from the top of the thread.
  return (
    <div className={className} aria-hidden="true">
      <div ref={swayRef} className={styles.spiderInner} />
    </div>
  );
};

export default HangingSpider;
